import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {UserHomepageControllerContext, UserHomepageStateContext} from "../../index";
import {UserHomepageControllerEvents, UserHomepageViewMode} from "../../models";
import {Fade, SvgIcon} from "@mui/material";
import Button from "../../../../components/base/button/button";
import CreateNewUserHomepageEntity from "../dialogs/create-new-entity";
import useIsMounted from "../../../../hooks/use-is-mounted";
import UserHomepageControlPanelTabs from "./components/tabs";
import Api from "../../../../../core/services/api/api";
import {ReactComponent as ToggleIcon} from "../../../../../assets/images/warranty-statuses-reasons/toggle.svg";
import classnames from "classnames";
import IconButton from "../../../../components/base/button/icon_button";
import {createUUId} from "../../../../../core/services/utils/utils";
import useWindowViewportWidth from "../../../../hooks/use-window/viewport-width";
import ObserversService from "../../../../../core/services/observers";
import {Add} from '@mui/icons-material';
import debounce from "lodash/debounce";
import useCanManageHomepage from "../../hooks/use-can-manage-homepage";
import useAccessLevels from "../../../../hooks/use-access-levels";

const UserHomepageControlPanel = ({showTabCards = false, maxTabs = 3}) => {
    const controller = useContext(UserHomepageControllerContext);
    const state = useContext(UserHomepageStateContext);
    const [createTabDialog, setCreateTabDialog] = useState(false);
    // const [contextState, setContextState] = useState(DefaultUserHomepageControlPanelContextValue);
    const newComponent = useRef(undefined);
    const isMounted = useIsMounted();
    const canManageHomepage = useCanManageHomepage();
    const {packages} = useAccessLevels();
    const hasHomepageManageFlag =
        packages.clientPlatform.manageWebHomepageEditions ||
        packages.clientPlatform.managePhoneHomepageEditions;

    const loading = useMemo(() => !state?.updatedData,
        [state?.updatedData])
    const showCreateNewTab = useMemo(() => (state.updatedData?.tabs?.length ?? 0) < maxTabs && !loading,
        [state.updatedData?.tabs?.length, maxTabs, loading])
    const controlPanelCollapsed = useMemo(() =>
            state.viewMode === UserHomepageViewMode.preview || state.controlPanel.collapsed,
        [state.viewMode, state.controlPanel.collapsed])


    /**
     * With each change in the updatedDate of the controller:
     * - attaches an event listener for creation of components so that they can be selected and focused.
     */
    useEffect(() => {
        controller.on(UserHomepageControllerEvents.onComponentCreated, awaitStateUpdateOnComponentCreation);
        return () => {
            controller.off(UserHomepageControllerEvents.onComponentCreated, awaitStateUpdateOnComponentCreation);
        }
    }, [controller, state.updatedData])

    /**
     * With each change in the updatedData of the state:
     * - checks if a new component has been created, and if so, awaits its appearance in the ui and then scrolls to it.
     */
    useEffect(() => {
        if (!newComponent.current)
            return;
        const component = state?.updatedData?.tabs?.find(e => e.id === newComponent.current.tabId)?.components?.find(e => e.id === newComponent.current.componentId);
        if (!component)
            return;

        newComponent.current = undefined;
        setTimeout(() => {
            document
                .getElementById(component.controlPanelElementId)
                ?.scrollIntoView(true);
        }, 500)
    }, [state.updatedData])

    /**
     * Awaits the update of the state before focusing the newly created component
     * @param {UserHomepageEventEmitter} emitter
     * @param {number} tabId
     * @param {number} componentId
     */
    const awaitStateUpdateOnComponentCreation = (emitter, tabId, componentId) => {
        newComponent.current = {tabId, componentId};
    }

    /**
     * Creates a new tab for the current homepage edition.
     *
     * @param formValues
     * @return {Promise<boolean | undefined>}
     */
    const createNewTab = async (formValues) => {
        if (!canManageHomepage || !hasHomepageManageFlag) return;
        const forApi = {
            editionId: state.id,
            name: formValues?.title,
        }
        const response = await Api.createHomepageEditionTab(forApi);
        if (!isMounted())
            return;

        if (response?.resultFlag) {
            controller.createTab(response.data);
        }
        return response?.resultFlag;
    }


    /**
     * The control-panel element.
     * @type {JSX.Element}
     */
    const controlPanel = useMemo(() => (
        <div
            className={classnames(
                'user-homepage-control-panel',
                {
                    'collapsed': controlPanelCollapsed
                }
            )}
            id={state?.controlPanelElementId}
        >
            {
                <div className={'tabs-topbar'}>
                    {
                        showTabCards &&
                        <p className={'title'}>
                            Tabs
                        </p>
                    }
                    <Fade in={showCreateNewTab}>
                        <Button
                            variant={'text'}
                            color={'primary'}
                            onClick={() => setCreateTabDialog(true)}
                            className={'d-flex align-items-center'}
                        >
                            <Add className={'mr-1'}/>
                            <p>
                                Add a new tab
                            </p>
                        </Button>
                    </Fade>
                </div>
            }
            <UserHomepageControlPanelTabs
                onlyRenderComponents={!showTabCards}
            />
        </div>
    ), [showTabCards, state?.controlPanelElementId, showCreateNewTab, controlPanelCollapsed, state.viewMode, loading])

    return (
        <>
            {/*<UserHomepageControlPanelContext.Provider value={contextState}>*/}
            <div className={'user-homepage-control-panel-container'}>
                {controlPanel}
                <ControlPanelToggler
                    collapsed={controlPanelCollapsed}
                    hidden={state.viewMode === UserHomepageViewMode.preview}
                    elementId={state.controlPanelElementId}
                />
            </div>
            {/*</UserHomepageControlPanelContext.Provider>*/}
            <CreateNewUserHomepageEntity
                label={"Tab"}
                open={createTabDialog}
                close={() => setCreateTabDialog(false)}
                create={createNewTab}
            />
        </>
    )
}

const ControlPanelToggler = ({collapsed, elementId, hidden}) => {
    const viewportWidth = useWindowViewportWidth();
    const controller = useContext(UserHomepageControllerContext);
    const lineId = useRef(createUUId(true));
    const buttonId = useRef(createUUId(true));
    const isMounted = useIsMounted();


    /**
     * With each change in the elementId:
     * - attaches an observer that would sync the height of the toggler line with the height of the control panel.
     */
    useEffect(() => {
        const element = document.getElementById(elementId);
        if (!element)
            return;
        const throttledCallback = debounce((entries, observer) => syncHeight(entries, observer), 100);
        const observer = ObserversService.newResizeObserver(throttledCallback);
        ObserversService.observeResizeObserver(observer, element);
        throttledCallback([element]);
        return () => {
            ObserversService.disconnectResizeObserver(observer);
        }
    }, [elementId])

    /**
     * Updates the height of the toggler body with the height of the control panel.
     * @param {ResizeObserverEntry[] | Element[]} entries
     */
    const syncHeight = (entries) => {
        requestAnimationFrame(() => {
            if (!isMounted())
                return;
            if (!entries?.length)
                return;
            const entry = entries[0];
            const entryHeight = entry?.contentRect?.height ?? entry?.getBoundingClientRect()?.height ?? 0;
            const line = document.getElementById(lineId.current);
            const button = document.getElementById(buttonId.current);
            if (!line || !button)
                return;
            const lineHeight = entryHeight - button.getBoundingClientRect().height;
            line.style.height = `${lineHeight}px`;
        })
    }
    /**
     * Toggles the control panel.
     */
    const toggleControlPanel = () => {
        controller.toggleControlPanelExpanded(!collapsed);
    }

    if (['xs', 'sm', 'md'].includes(viewportWidth) || hidden) {
        return (
            <>
            </>
        );
    }

    return (
        <>
            <div className={'user-homepage-control-panel-toggler'}>
                <IconButton
                    id={buttonId.current}
                    className={'toggle-button'}
                    color={'paper.secondary'}
                    onPressed={toggleControlPanel}
                >
                    <SvgIcon component={ToggleIcon}
                             className={classnames(
                                 'icon normal toggle-icon',
                                 {'open': !collapsed}
                             )}
                    />
                </IconButton>
                <div id={lineId.current} className={'line'}/>
            </div>
        </>
    );
}


export default UserHomepageControlPanel;
